import type { Company, CompanyStatus } from '@l2u/ui/lib/types.ts';
import { COMPANY_STATUSES } from '@l2u/ui/lib/companyStatus.ts';
import { pruneToOptions } from './filterStorage.ts';

/** Value of the status filter — a concrete status, or `'all'` for no filter. */
export type StatusFilter = CompanyStatus | 'all';

/**
 * Exactly the company fields the filter reads.
 *
 * Stated as its own type, rather than taking a whole `Company`, because the
 * admin list payload no longer carries every column
 * (ADMIN_COMPANY_LIST_COLUMNS). Declaring the real requirement means this
 * module compiles against the narrow list row AND the full `Company`, and a
 * future search field added below fails to compile until it is added here —
 * which is the point: the search list and this type must not drift.
 */
export type FilterableCompany = Pick<
  Company,
  | 'status'
  | 'contractor_types'
  | 'cluster_regions'
  | 'stations'
  | 'company_name'
  | 'contact_name'
  | 'email'
  | 'abn_entity_name'
  | 'legal_entity_name'
  | 'vendor_number'
  | 'contract_numbers'
  | 'territory'
  | 'primary_contact_name'
  | 'contact_email'
>;

/**
 * Sentinel filter value matching companies whose field is empty/null. A
 * double-underscore token can't collide with real human-entered free text.
 */
export const FIELD_NOT_SET = '__none__';

/** The company-info fields the list can filter on. All three are string[]
 *  fields. */
export type FilterableField = 'contractor_types' | 'cluster_regions' | 'stations';

export type CompanyFilters = {
  /** Free-text search matched against company (trading) name, contact name and
   *  email, ABN and vendor-entered legal entity names, vendor number, contract
   *  number, territory and the company-filled primary contact name/email;
   *  '' = no search. */
  query: string;
  /** 'all' = every active status (deactivated hidden); 'any' = no status
   *  condition at all — used by the faceted pill counts, which must see
   *  deactivated rows to count them under their own pill. */
  status: StatusFilter | 'any';
  /** Selected contractor types — OR within the field. `[]` = no filter; a
   *  FIELD_NOT_SET entry matches companies whose field is empty/whitespace;
   *  other entries are exact (trimmed) matches. */
  contractorType: string[];
  /** Same contract as contractorType. */
  clusterRegion: string[];
  /** Same contract as contractorType. */
  station: string[];
};

// [] → no filter (field condition disabled). Otherwise the vendor matches when
// ANY of its (trimmed, non-empty) values is in the selected set — OR within the
// field — and a FIELD_NOT_SET entry matches a NULL/empty/whitespace-only list.
// Values are stored trimmed by the write paths, but trim here too in case one
// ever differs.
function matchesListField(values: string[] | null, filters: string[]): boolean {
  if (filters.length === 0) return true;
  const list = (values ?? []).map((v) => v.trim()).filter((v) => v !== '');
  if (list.length === 0) return filters.includes(FIELD_NOT_SET);
  return list.some((v) => filters.includes(v));
}

/**
 * Filter the admin company list. All conditions AND together: a free-text query
 * (matched against company (trading) name, contact name and email, ABN and
 * vendor-entered legal entity names, vendor number, contract number, territory
 * and the company-filled primary contact name/email), a status, and
 * set-membership matches on the contractor type / cluster region / station
 * fields (OR within each field). An empty array disables that field's condition.
 * For status: `'all'` keeps every *active* status (deactivated rows hidden),
 * `'any'` applies no status condition at all (deactivated rows included — used by
 * the faceted pill counts), and a concrete status keeps only that status.
 */
export function filterCompanies<T extends FilterableCompany>(
  companies: T[],
  filters: CompanyFilters,
): T[] {
  const q = filters.query.trim().toLowerCase();
  return companies.filter((c) => {
    if (filters.status === 'any') {
      // no status condition — faceted counts need deactivated rows included
    } else if (filters.status === 'all') {
      // 'all' means every *active* status — deactivated companies appear only
      // when their pill is selected explicitly.
      if (c.status === 'deactivated') return false;
    } else if (c.status !== filters.status) {
      return false;
    }
    if (!matchesListField(c.contractor_types, filters.contractorType)) return false;
    if (!matchesListField(c.cluster_regions, filters.clusterRegion)) return false;
    if (!matchesListField(c.stations, filters.station)) return false;
    if (!q) return true;
    // Nullable company-filled / admin fields default to '' so a missing value
    // never matches (and never throws).
    return (
      c.company_name.toLowerCase().includes(q) ||
      c.contact_name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.abn_entity_name ?? '').toLowerCase().includes(q) ||
      (c.legal_entity_name ?? '').toLowerCase().includes(q) ||
      (c.vendor_number ?? '').toLowerCase().includes(q) ||
      (c.contract_numbers ?? []).some((n) => n.toLowerCase().includes(q)) ||
      (c.territory ?? '').toLowerCase().includes(q) ||
      (c.primary_contact_name ?? '').toLowerCase().includes(q) ||
      (c.contact_email ?? '').toLowerCase().includes(q)
    );
  });
}

export type FieldValues = {
  /** Distinct non-empty trimmed values, deduped and localeCompare-sorted. */
  values: string[];
  /** True when at least one company has the field empty — drives "(Not set)". */
  hasNotSet: boolean;
};

/**
 * Distinct values of a free-text company-info field across the *unfiltered*
 * list, for the filter dropdowns (options stay stable while other filters
 * narrow the view). Dedupe is exact/case-sensitive: contractor types come from
 * a curated dropdown, and merging "North"/"north" heuristically would be
 * unpredictable.
 */
// Trimmed, non-empty entries of a filterable field — every filterable field is
// a list, so a vendor contributes one entry per type / region / station.
function fieldEntries(c: Pick<Company, FilterableField>, field: FilterableField): string[] {
  const list = c[field] ?? [];
  return list.map((v) => v.trim()).filter((v) => v !== '');
}

export function distinctFieldValues(
  companies: Pick<Company, FilterableField>[],
  field: FilterableField,
): FieldValues {
  const set = new Set<string>();
  let hasNotSet = false;
  for (const c of companies) {
    const entries = fieldEntries(c, field);
    if (entries.length === 0) hasNotSet = true;
    for (const v of entries) set.add(v);
  }
  return { values: [...set].sort((a, b) => a.localeCompare(b)), hasNotSet };
}

/**
 * FieldValues for a field whose dropdown options are curated in Settings
 * (branding) rather than derived from the data. Options keep their configured
 * order (matching the Settings editor and the create-company modal), trimmed,
 * with empties and duplicates dropped. (Settings already persists the list
 * through `normalizeStringList`, which trims/dedupes — the re-normalisation
 * here is belt-and-braces for any other write path.) `hasNotSet` reports whether
 * any company currently misses the field (informational; the "(Not set)" filter
 * is offered for these fields regardless, see parseFieldFilterParam).
 */
export function configuredFieldValues(
  configured: string[],
  companies: Pick<Company, FilterableField>[],
  field: FilterableField,
): FieldValues {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const raw of configured) {
    const v = raw.trim();
    if (v !== '' && !seen.has(v)) {
      seen.add(v);
      values.push(v);
    }
  }
  return { values, hasNotSet: companies.some((c) => fieldEntries(c, field).length === 0) };
}

export type StatusCounts = {
  /** Count per status — every status in COMPANY_STATUSES is present (0 when none). */
  counts: Record<CompanyStatus, number>;
  /** Number of *active* companies — deactivated rows count only under their own pill. */
  total: number;
};

// Maps a raw ?status= query value to a StatusFilter, defaulting unknown/empty
// values to 'all' so a hand-edited URL can never crash the filter.
export function parseStatusFilterParam(raw: string | null): StatusFilter {
  if (raw && (COMPANY_STATUSES as readonly string[]).includes(raw)) {
    return raw as StatusFilter;
  }
  return 'all';
}

/**
 * Maps the repeatable raw ?type= / ?region= / ?station= query values to a
 * validated, deduped field-filter array. FIELD_NOT_SET always passes through —
 * it's an admin-meaningful "find vendors missing this field" filter, offered
 * regardless of whether any vendor currently has it empty (it simply returns no
 * rows when none do). A concrete value passes only while it exists in the data;
 * anything else (hand-edited URL, stale link after the last matching company was
 * deleted) is dropped so the control can never silently disagree with the table.
 * Order follows `allowed.values` (with FIELD_NOT_SET last) for a deterministic URL.
 */
export function parseFieldFilterParam(raw: string[], allowed: FieldValues): string[] {
  const wanted = new Set(raw);
  const out = allowed.values.filter((v) => wanted.has(v));
  if (wanted.has(FIELD_NOT_SET)) out.push(FIELD_NOT_SET);
  return out;
}

/**
 * Free-text match for the admin Dashboard search: company (trading) name,
 * contact name, email, ABN legal entity name, vendor number, contract numbers
 * and territory (case-insensitive, trimmed). An empty query matches everything.
 */
export function matchesCompanyQuery(
  c: Pick<
    Company,
    | 'company_name'
    | 'contact_name'
    | 'email'
    | 'abn_entity_name'
    | 'vendor_number'
    | 'contract_numbers'
    | 'territory'
  >,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    c.company_name.toLowerCase().includes(q) ||
    c.contact_name.toLowerCase().includes(q) ||
    c.email.toLowerCase().includes(q) ||
    (c.abn_entity_name ?? '').toLowerCase().includes(q) ||
    (c.vendor_number ?? '').toLowerCase().includes(q) ||
    (c.contract_numbers ?? []).some((n) => n.toLowerCase().includes(q)) ||
    (c.territory ?? '').toLowerCase().includes(q)
  );
}

/**
 * Lightweight filter spec for the Dashboard and Expiring Documents views.
 * Each dimension is an array of selected values; an empty array means "no
 * constraint on that dimension". All active dimensions AND together.
 */
export type CompanyFilter = {
  contractorTypes: string[];
  clusterRegions: string[];
  stations: string[];
};

export type CompanyFilterable = {
  contractor_types?: string[] | null;
  cluster_regions?: string[] | null;
  stations?: string[] | null;
};

/**
 * Returns true when company `c` satisfies every active dimension of filter `f`.
 * contractor_types, cluster_regions and stations are all array fields — the
 * company matches a dimension when its values and the selected values
 * intersect. An empty selection disables that dimension (matches everything).
 */
export function matchesCompanyFilter(c: CompanyFilterable, f: CompanyFilter): boolean {
  if (
    f.contractorTypes.length > 0 &&
    !(c.contractor_types ?? []).some((t) => f.contractorTypes.includes(t))
  )
    return false;
  if (
    f.clusterRegions.length > 0 &&
    !(c.cluster_regions ?? []).some((r) => f.clusterRegions.includes(r))
  )
    return false;
  if (f.stations.length > 0 && !(c.stations ?? []).some((s) => f.stations.includes(s)))
    return false;
  return true;
}

function stringArray(v: unknown): string[] | null {
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : null;
}

/**
 * Validator for a localStorage-persisted CompanyFilter (2026-08-06 design,
 * Task 2). Tolerates extra keys (the Dashboard stores its free-text query
 * alongside the three dimensions); rejects any missing/mis-typed dimension so
 * a stale shape from an old release is discarded, not applied.
 */
export function parseCompanyFilter(raw: unknown): CompanyFilter | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const contractorTypes = stringArray(r.contractorTypes);
  const clusterRegions = stringArray(r.clusterRegions);
  const stations = stringArray(r.stations);
  if (!contractorTypes || !clusterRegions || !stations) return null;
  return { contractorTypes, clusterRegions, stations };
}

/**
 * The live option lists a stored CompanyFilter is reconciled against — one per
 * dimension, mirroring components/FilterBar.tsx's FilterBarOptions.
 */
export type CompanyFilterOptions = CompanyFilter;

/**
 * Reconcile a restored filter against the live option lists, dropping values
 * Settings no longer offers. Returns `filter` BY REFERENCE when nothing
 * changed, so a caller can feed the result straight to setState without
 * causing a re-render or a spurious write-through.
 *
 * Reconciling is SKIPPED entirely when every option list is empty.
 * PortalSettingsProvider (packages/ui/src/lib/portalSettings.tsx) reports a
 * failed /api/settings fetch as success-with-nothing — its catch sets
 * settings={} and its finally sets loading=false — so an all-empty option set
 * is indistinguishable from a transient network failure. Pruning against it
 * would empty the filter, and a caller that writes state through to storage
 * would then delete the saved key: the admin's filters, gone for good because
 * one request failed. Nothing to reconcile against means nothing to reconcile.
 * This is strictly the safer side to err on — with zero configured options the
 * dropdowns are empty, so no filter could have been set through the UI anyway,
 * and a surviving stale value can still be cleared by hand.
 *
 * A single empty dimension alongside configured ones is NOT suspect (Settings
 * loaded, that dimension is genuinely unconfigured) and is pruned normally.
 */
export function reconcileCompanyFilter(
  filter: CompanyFilter,
  options: CompanyFilterOptions,
): CompanyFilter {
  if (
    options.contractorTypes.length === 0 &&
    options.clusterRegions.length === 0 &&
    options.stations.length === 0
  ) {
    return filter;
  }
  const next: CompanyFilter = {
    contractorTypes: pruneToOptions(filter.contractorTypes, options.contractorTypes),
    clusterRegions: pruneToOptions(filter.clusterRegions, options.clusterRegions),
    stations: pruneToOptions(filter.stations, options.stations),
  };
  // pruneToOptions only ever removes, so equal lengths imply equal sets.
  const unchanged =
    next.contractorTypes.length === filter.contractorTypes.length &&
    next.clusterRegions.length === filter.clusterRegions.length &&
    next.stations.length === filter.stations.length;
  return unchanged ? filter : next;
}

/** The Dashboard's stored free-text query, read from the same stored object. */
export function parseStoredQuery(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const q = (raw as { query?: unknown }).query;
  return typeof q === 'string' ? q : null;
}

/**
 * Tally companies by status for the filter strip's count badges. Every status is
 * seeded to 0 first, so the strip renders a stable, complete set of pills without
 * each one having to guard for a missing key.
 */
export function countByStatus(companies: Pick<Company, 'status'>[]): StatusCounts {
  const counts = Object.fromEntries(COMPANY_STATUSES.map((s) => [s, 0])) as Record<
    CompanyStatus,
    number
  >;
  let total = 0;
  for (const c of companies) {
    if (c.status in counts) counts[c.status] += 1;
    if (c.status !== 'deactivated') total += 1;
  }
  return { counts, total };
}

/**
 * The filtered rows AND the faceted status counts, from ONE pass over the list.
 *
 * The Vendors page needs both, and they used to come from two separate
 * filterCompanies calls that differed in exactly one argument: the list uses the
 * selected status, the pill counts use 'any' (a facet never filters its own
 * counts, or clicking a pill would zero every other one). Everything else — the
 * ten-field free-text search and the three set-membership filters — was computed
 * twice per keystroke.
 *
 * The saving comes from the containment relationship: the faceted set is the
 * SUPERSET that ignores status, so the list set is just the faceted set with the
 * status condition applied. Run the expensive conditions once to get the
 * superset, count it, then narrow. The status re-check is a scalar comparison,
 * so this is ~half the work of the old two-call shape at any list size.
 *
 * Returns the rows unsorted — ordering is the caller's concern (the Vendors page
 * sorts by the rendered cell text, which this module knows nothing about).
 */
export function filterWithStatusCounts<T extends FilterableCompany>(
  companies: T[],
  filters: CompanyFilters,
): { rows: T[]; counts: StatusCounts } {
  // status: 'any' — the faceted superset, deactivated rows included so the
  // Deactivated pill can show a real count.
  const faceted = filterCompanies(companies, { ...filters, status: 'any' });
  const counts = countByStatus(faceted);

  // Now apply only the status condition the caller actually asked for. Kept
  // identical in meaning to filterCompanies' own status branch: 'any' keeps
  // everything, 'all' keeps every ACTIVE status, a concrete status keeps itself.
  const { status } = filters;
  const rows =
    status === 'any'
      ? faceted
      : status === 'all'
        ? faceted.filter((c) => c.status !== 'deactivated')
        : faceted.filter((c) => c.status === status);

  return { rows, counts };
}
